import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Icons from '@shared/components/core/images/Icons';
import { ButtonSize, ICoreButtonProps } from '@shared/models/components/ICoreButtonProps';
import { forwardRef } from 'react';
import { NavLink } from 'react-router';
import { aaaSizeStyles, buttonBaseStyles, disabledButtonStyles, sizeStyles } from './CommonButtonStyles';

// ─────────────────────────────────────────────
// VARIANT STYLES
// Each variant is a self-contained style block.
// They reference theme tokens only — no raw values.
// The base structure (size, spacing, touch target)
// comes from buttonBaseStyles and sizeStyles.
// ─────────────────────────────────────────────

const variantStyles = {
    primary: ({ theme }: { theme: any }) => css`
        font-weight: 700;
        background: ${theme.button.primary.background};
        ${theme.button.primary.backgroundImage &&
        `background-image: linear-gradient(54deg, #062844 4.52%, #4b2b8e 59.43%, #ee486f 125.41%)`};
        border: 1px solid ${theme.button.primary.border};
        color: ${theme.button.primary.text};
        text-decoration: none;
        &:hover {
            color: ${theme.button.primary.hoverText};
            background: ${theme.button.primary.hoverBackground};
            svg {
                color: ${theme.button.primary.icon};
            }
        }
    `,
    secondary: ({ theme }: { theme: any }) => css`
        font-weight: 700;
        background: ${theme.button.secondary.background};
        border: 1px solid ${theme.button.secondary.border};
        color: ${theme.button.secondary.text};
        text-decoration: none;
        &:hover {
            color: ${theme.button.secondary.hoverText};
            background: ${theme.button.secondary.hoverBackground};
            svg {
                color: ${theme.button.secondary.icon};
            }
        }
    `,
    accent: ({ theme }: { theme: any }) => css`
        font-weight: 300;
        background: ${theme.button.accent.background};
        border: 1px solid transparent;
        border-radius: 2rem;
        color: ${theme.button.accent.text};
        text-decoration: none;
        &:hover {
            border-color: ${theme.button.accent.border};
            background: ${theme.button.accent.hoverBackground};
            color: ${theme.button.accent.hoverText};
            svg {
                color: ${theme.button.accent.icon};
            }
        }
    `,
    simple: ({ theme }: { theme: any }) => css`
        background: ${theme.button.simple.background};
        color: ${theme.button.simple.text};
        border: none;
        text-decoration: underline;
        text-decoration-color: ${theme.button.simple.text};
        min-width: fit-content;
        &:hover {
            text-decoration-color: ${theme.button.simple.hoverText};
            box-shadow: 0 0 0 1px ${theme.button.simple.text};
            filter: none;
        }
    `,
    quick: ({ theme }: { theme: any }) => css`
        font-weight: 400;
        font-family: 'Archivo', sans-serif;
        background: ${theme.button.quick.background};
        border: 1px solid ${theme.button.quick.border};
        border-radius: 1.875rem;
        color: ${theme.button.quick.text};
        text-decoration: none;
        svg {
            color: ${theme.button.quick.border};
        }
        &:hover {
            background: ${theme.button.quick.hoverBackground};
            color: ${theme.button.quick.hoverText};
            text-decoration: underline;
            text-decoration-color: ${theme.button.quick.border};
            svg {
                color: ${theme.button.quick.icon};
            }
        }
    `,
    dashedAccent: ({ theme }: { theme: any }) => css`
        font-weight: 300;
        background: ${theme.button.accent.background};
        border: 1px dashed ${theme.color.accent};
        border-radius: 2rem;
        color: ${theme.color.accent};
        min-width: fit-content;
        text-decoration: none;
        &:hover {
            border-color: ${theme.button.accent.border};
            background: ${theme.button.accent.hoverBackground};
            color: ${theme.button.accent.border};
            svg {
                color: ${theme.button.accent.border};
            }
        }
    `,
    solidAccent: ({ theme }: { theme: any }) => css`
        font-weight: 300;
        background: ${theme.button.accent.background};
        border: 1px solid ${theme.color.accent};
        border-radius: 2rem;
        color: ${theme.color.accent};
        text-decoration: none;
        &:hover {
            border-color: ${theme.button.accent.border};
            background: ${theme.button.accent.hoverBackground};
            color: ${theme.button.accent.border};
            svg {
                color: ${theme.button.accent.border};
            }
        }
    `,
} as const;

export type ButtonVariant = keyof typeof variantStyles;

// ─────────────────────────────────────────────
// STYLED PRIMITIVE
// Single styled element — variant and size are
// applied as composed styles, not separate
// components per variant.
// ─────────────────────────────────────────────

type ButtonElement = HTMLButtonElement | HTMLAnchorElement;

type SharedStyleProps = {
    $color: ButtonVariant;
    $size: ButtonSize;
    disabled?: boolean;
};

// Shared style function — single source of truth for all three primitives
const sharedStyles = ({ theme, $color, $size, disabled }: SharedStyleProps & { theme: any }) => css`
    ${buttonBaseStyles}
    ${variantStyles[$color]?.({ theme })}
    ${theme.scaleSize > 16 ? aaaSizeStyles[$size] : sizeStyles[$size]}
    ${disabled && disabledButtonStyles}
`;

const shouldForwardProp = (prop: string) => !['$color', '$size'].includes(prop);

// Three typed primitives sharing one style function.
// button → <button>, href → <a>, to → <NavLink>
const StyledButton = styled('button', { shouldForwardProp })<SharedStyleProps>(sharedStyles);

const StyledAnchor = styled('a', { shouldForwardProp })<SharedStyleProps>(sharedStyles);

const StyledNavLink = styled(NavLink, { shouldForwardProp })<SharedStyleProps>(sharedStyles);

// ─────────────────────────────────────────────
// CORE BUTTON
// Renders as button, <a>, or NavLink depending
// on props. All three share the same styled
// primitive via the `as` prop — no duplicate
// styled components needed.
// ─────────────────────────────────────────────

const Button = forwardRef<ButtonElement, ICoreButtonProps>(
    (
        {
            type = 'button',
            $color = 'primary',
            $size = 'medium',
            disabled = false,
            onClick,
            href,
            to,
            target,
            iconSize = 1.5,
            startIcon,
            endIcon,
            startComponent,
            endComponent,
            children,
            ...restProps
        },
        ref
    ) => {
        // Auto-apply external link icon for blank targets
        if (target === '_blank' && href?.startsWith('http')) {
            endIcon = endIcon ?? 'externalLink';
        }
        // Default anchor target
        if (href && !target) target = '_self';

        const sharedProps = {
            $color,
            $size,
            disabled,
            target,
            className: 'core-button',
            ...restProps,
        };

        const content = (
            <>
                {startComponent}
                {startIcon && <Icons name={startIcon} size={iconSize} />}
                {children}
                {endComponent}
                {endIcon && <Icons name={endIcon} size={iconSize} />}
            </>
        );

        if (href) {
            return (
                <StyledAnchor
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    className="core-button"
                    href={href}
                    target={target}
                    $color={$color}
                    $size={$size}
                    {...restProps}>
                    {content}
                </StyledAnchor>
            );
        }

        if (to) {
            return (
                <StyledNavLink
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    className="core-button"
                    viewTransition
                    to={to}
                    $color={$color}
                    $size={$size}
                    {...restProps}>
                    {content}
                </StyledNavLink>
            );
        }

        return (
            <StyledButton
                ref={ref as React.Ref<HTMLButtonElement>}
                className="core-button"
                type={type}
                onClick={onClick}
                disabled={disabled}
                $color={$color}
                $size={$size}
                {...restProps}>
                {content}
            </StyledButton>
        );
    }
);

Button.displayName = 'CoreButton';

export default Button;
